import React from 'react';
import { toast } from 'sonner';
import { ArrowRightLeft, CalendarPlus, Download, FilePlus2, Link2, Loader2, Pencil, Trash2 } from 'lucide-react';
import { Button } from '../../ui/button';
import { adminApi } from '../../../lib/adminAuth';
import NewProposalDialog from './NewProposalDialog';
import ProposalEditor from './ProposalEditor';
import ConvertDialog from './ConvertDialog';
import StatusPill from './StatusPill';
import { IconBtn } from './ContractsPanel';
import { isExpired } from './ProposalEditor';
import { copyShareLink, downloadPdf, errMsg, fileName, fmtDate, inr } from './docUtils';

const FILTERS = ['all', 'draft', 'sent', 'viewed', 'accepted', 'converted', 'declined', 'expired'];

export default function ProposalsPanel({ settings, onOpenContract, canDelete }) {
  const [rows, setRows] = React.useState([]);
  const [counts, setCounts] = React.useState({});
  const [filter, setFilter] = React.useState('all');
  const [loading, setLoading] = React.useState(true);
  const [editing, setEditing] = React.useState(null);
  const [creating, setCreating] = React.useState(false);
  const [converting, setConverting] = React.useState(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    try { const r = await adminApi.get('/api/admin/proposals', { params: { status: filter } }); setRows(r.data.proposals); setCounts(r.data.counts); }
    catch (e) { toast.error(errMsg(e, 'Could not load proposals')); }
    finally { setLoading(false); }
  }, [filter]);
  React.useEffect(() => { load(); }, [load]);
  const closeEditor = React.useCallback(() => setEditing(null), []);
  const total = Object.values(counts).reduce((a, b) => a + b, 0);

  const remove = async (p) => {
    if (!window.confirm(`Delete ${p.proposal_number}?`)) return;
    try { await adminApi.delete(`/api/admin/proposals/${p._id}`); toast.success('Proposal deleted'); load(); }
    catch (e) { toast.error(errMsg(e, 'Could not delete')); }
  };

  return (
    <div className="soft-card p-4 sm:p-5" data-testid="proposals-panel">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-[#1a1a2e]">Proposals</h3>
          <p className="text-xs text-slate-500">Branded proposals for prospects or clients. Once accepted, convert to a client and a draft contract in one click.</p>
        </div>
        <Button size="sm" onClick={() => setCreating(true)} className="bg-gray-900 hover:bg-gray-800" data-testid="proposals-new"><FilePlus2 className="w-4 h-4 mr-1.5" /> New proposal</Button>
      </div>
      <div className="flex gap-1.5 flex-wrap mb-3">
        {FILTERS.map((f) => (
          <button key={f} type="button" onClick={() => setFilter(f)} data-testid={`proposals-filter-${f}`}
            className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${filter === f ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'}`}>
            {f === 'all' ? 'All' : f[0].toUpperCase() + f.slice(1)} <span className="opacity-60">{f === 'all' ? total : counts[f] || 0}</span>
          </button>
        ))}
      </div>
      {loading ? <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-fuchsia-600" /></div> : rows.length === 0 ? (
        <p className="py-10 text-center text-sm text-slate-500" data-testid="proposals-empty">No proposals here yet.</p>
      ) : (
        <div className="overflow-x-auto -mx-4 sm:mx-0">
          <table className="w-full text-sm min-w-[760px]" data-testid="proposals-table">
            <thead><tr className="text-left text-[11px] uppercase tracking-wide text-slate-400 border-b border-slate-100">
              <th className="py-2 px-3">Proposal</th><th className="py-2 px-3">For</th><th className="py-2 px-3 text-right">Monthly</th>
              <th className="py-2 px-3">Status</th><th className="py-2 px-3">Valid until</th><th className="py-2 px-3 text-right">Actions</th>
            </tr></thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p._id} className="border-b border-slate-50 hover:bg-slate-50/60" data-testid={`proposal-row-${p._id}`}>
                  <td className="py-2.5 px-3"><button type="button" onClick={() => setEditing(p._id)} className="font-semibold text-slate-800 hover:text-fuchsia-700" data-testid={`proposal-open-${p._id}`}>{p.proposal_number}</button>
                    <p className="text-[11px] text-slate-400">{p.recipient_type === 'client' ? 'Client' : 'Prospect'}</p></td>
                  <td className="py-2.5 px-3">{p.brand_name || p.recipient_company}<p className="text-[11px] text-slate-400">{p.recipient_name}</p></td>
                  <td className="py-2.5 px-3 text-right tabular-nums">{inr(p.monthly_total)}</td>
                  <td className="py-2.5 px-3"><StatusPill status={p.effective_status} testId={`proposal-status-${p._id}`} />{p.changed_since_sent && ['sent', 'viewed'].includes(p.status) && <span className="ml-1 text-[10px] font-semibold text-amber-700" data-testid={`proposal-changed-${p._id}`}>changed</span>}</td>
                  <td className="py-2.5 px-3 text-xs text-slate-500">{fmtDate(p.valid_until)}</td>
                  <td className="py-2.5 px-3"><div className="flex justify-end gap-0.5">
                    <IconBtn title="Open" onClick={() => setEditing(p._id)} testId={`proposal-edit-${p._id}`}><Pencil /></IconBtn>
                    <IconBtn title="Download PDF" onClick={() => downloadPdf(`/api/admin/proposals/${p._id}/pdf`, fileName(p.proposal_number))} testId={`proposal-pdf-${p._id}`}><Download /></IconBtn>
                    {isExpired(p) && !['accepted', 'converted', 'declined'].includes(p.status) && <IconBtn title="Extend by 15 days" onClick={async () => { try { await adminApi.post(`/api/admin/proposals/${p._id}/extend`, { days: 15 }); toast.success('Extended by 15 days'); load(); } catch (e) { toast.error(errMsg(e, 'Could not extend')); } }} testId={`proposal-extend-${p._id}`}><CalendarPlus /></IconBtn>}
                    {!['converted', 'declined'].includes(p.status) && !isExpired(p) && <IconBtn title="Copy accept link" onClick={async () => { await copyShareLink(`/api/admin/proposals/${p._id}/share-link`); load(); }} testId={`proposal-link-${p._id}`}><Link2 /></IconBtn>}
                    {!['converted', 'declined', 'draft'].includes(p.status) && <IconBtn title="Convert to contract" onClick={() => setConverting(p)} testId={`proposal-convert-${p._id}`}><ArrowRightLeft /></IconBtn>}
                    {p.status === 'converted' && p.converted_contract_id && <button type="button" onClick={() => onOpenContract?.(p.converted_contract_id)} className="text-xs font-semibold text-fuchsia-700 px-2" data-testid={`proposal-view-contract-${p._id}`}>Contract</button>}
                    {!['accepted', 'converted'].includes(p.status) && canDelete && <IconBtn title="Delete" danger onClick={() => remove(p)} testId={`proposal-delete-${p._id}`}><Trash2 /></IconBtn>}
                  </div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <NewProposalDialog open={creating} onClose={() => setCreating(false)} onCreated={(id) => { load(); setEditing(id); }} />
      <ProposalEditor proposalId={editing} onClose={closeEditor} onChanged={load} settings={settings} />
      <ConvertDialog proposal={converting} onClose={() => setConverting(null)} onDone={(r) => { setConverting(null); load(); onOpenContract?.(r.contract_id); }} />
    </div>
  );
}
